import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { CheckCircle } from 'lucide-react';
import {
  SpecialPaymentDto,
  SpecialPaymentStatus,
  PaymentMethod,
  specialPaymentsApi,
  getPaymentMethodName,
  formatCurrency,
  handleApiError
} from '../../services/specialPaymentsService';

interface MarkAsPaidModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  payment: SpecialPaymentDto | null;
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
  direction: rtl;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-heavy);
  width: 90%;
  max-width: 550px;
  max-height: 90vh;
  overflow-y: auto;
  direction: rtl;
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--success-light);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #16a34a;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const PaymentContext = styled.div`
  background: var(--primary-light);
  border-right: 4px solid var(--primary-color);
  padding: 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 24px;
`;

const ContextTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const ContextDetail = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  margin-top: 4px;
`;

const ContextAmount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--primary-color);
  margin-top: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const RequiredStar = styled.span`
  color: #dc2626;
  margin-right: 4px;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? '#dc2626' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc2626' : '#16a34a'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--success-light)'};
  }
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => props.hasError ? '#dc2626' : 'var(--border-color)'};
  border-radius: var(--border-radius-md);
  font-size: 14px;
  color: var(--text-primary);
  background: white;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc2626' : '#16a34a'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'var(--error-light)' : 'var(--success-light)'};
  }
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'success' | 'secondary' }>`
  background: ${props => props.variant === 'secondary' ? 'var(--border-color)' : '#16a34a'};
  color: ${props => props.variant === 'secondary' ? 'var(--text-primary)' : 'white'};
  border: none;
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MarkAsPaidModal: React.FC<MarkAsPaidModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  payment
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<number>(PaymentMethod.Cash);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    if (isOpen && payment) {
      // Set default values
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMethod(PaymentMethod.Cash);
      setErrors({});
    }
  }, [isOpen, payment]);

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};

    if (!paymentDate) {
      newErrors.paymentDate = 'יש להזין תאריך תשלום';
    }

    if (!paymentMethod) {
      newErrors.paymentMethod = 'יש לבחור אמצעי תשלום';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!payment) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await specialPaymentsApi.update(payment.id, {
        amount: payment.amount,
        description: payment.description,
        status: SpecialPaymentStatus.Paid,
        paymentMethod: paymentMethod,
        paymentDate: paymentDate,
        comments: payment.comments || ''
      });
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  if (!payment) return null;

  return (
    <ModalOverlay $isOpen={isOpen}>
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>
            <ModalTitle><CheckCircle size={20} style={{ marginLeft: '8px' }} /> סמן כשולם</ModalTitle>
            <CloseButton onClick={onClose} type="button">×</CloseButton>
          </ModalHeader>

          <ModalBody>
            {/* Payment Context */}
            <PaymentContext>
              <ContextTitle>{payment.description}</ContextTitle>
              {payment.comments && (
                <ContextDetail>{payment.comments}</ContextDetail>
              )}
              <ContextAmount>{formatCurrency(payment.amount)}</ContextAmount>
            </PaymentContext>

            {/* Payment Date */}
            <FormGroup>
              <Label>
                <RequiredStar>*</RequiredStar>
                תאריך תשלום
              </Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => {
                  setPaymentDate(e.target.value);
                  if (errors.paymentDate) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.paymentDate;
                      return newErrors;
                    });
                  }
                }}
                hasError={!!errors.paymentDate}
              />
              {errors.paymentDate && <ErrorMessage>{errors.paymentDate}</ErrorMessage>}
            </FormGroup>

            {/* Payment Method */}
            <FormGroup>
              <Label>
                <RequiredStar>*</RequiredStar>
                אמצעי תשלום
              </Label>
              <Select
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(parseInt(e.target.value));
                  if (errors.paymentMethod) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.paymentMethod;
                      return newErrors;
                    });
                  }
                }}
                hasError={!!errors.paymentMethod}
              >
                <option value={PaymentMethod.Cash}>{getPaymentMethodName(PaymentMethod.Cash)}</option>
                <option value={PaymentMethod.BankTransfer}>{getPaymentMethodName(PaymentMethod.BankTransfer)}</option>
                <option value={PaymentMethod.Check}>{getPaymentMethodName(PaymentMethod.Check)}</option>
                <option value={PaymentMethod.CreditCard}>{getPaymentMethodName(PaymentMethod.CreditCard)}</option>
                <option value={PaymentMethod.DirectDebit}>{getPaymentMethodName(PaymentMethod.DirectDebit)}</option>
                <option value={PaymentMethod.Other}>{getPaymentMethodName(PaymentMethod.Other)}</option>
              </Select>
              {errors.paymentMethod && <ErrorMessage>{errors.paymentMethod}</ErrorMessage>}
            </FormGroup>
          </ModalBody>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              ביטול
            </Button>
            <Button type="submit" variant="success" disabled={loading}>
              {loading ? 'שומר...' : '✓ סמן כשולם'}
            </Button>
          </ButtonContainer>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default MarkAsPaidModal;
